import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/authContext';
import { useWorkflowAreas } from '../../lib/access/useWorkflowAreas';
import { deliveryClient, morgansClient, projectDataClient } from '../../lib/connect/clients';
import { registryRecordHref } from './sharedRecordLinks';
const MIN_QUERY = 2;
const PER_SOURCE = 8;
const LIFECYCLE = {
    active: 'Active',
    parked: 'Parked',
    closed: 'Closed'
};
const NOT_QUERIED = Promise.resolve(null);
const has = (haystack, q)=>haystack.some((h)=>h?.toLowerCase().includes(q.toLowerCase()));
/**
 * Bounded discovery for the app search (C7A-10): permitted Companies, Vehicles,
 * Library items and labor categories, plus Programs, Pursuits and Landscapes
 * the viewer can reach, by plain text match — not semantic or AI search. A
 * program/pursuit/landscape source the viewer cannot reach is not queried (and
 * not reported); waits for reachability to resolve. Each source is the server's own permitted view; a source that rejects
 * (denied or unavailable) contributes nothing and is reported in `unavailable`,
 * so a denied record can never appear. Disabled below MIN_QUERY characters.
 */ export function useSharedRecordSearch(rawQuery) {
    const { user } = useAuth();
    const tenantId = user?.tenant_id ?? '';
    const query = rawQuery.trim();
    const { isLoading: areasLoading, reachable } = useWorkflowAreas();
    const { delivery, capture, opportunities } = reachable;
    return useQuery({
        queryKey: [
            'shared-record-search',
            tenantId,
            query,
            delivery,
            capture,
            opportunities
        ],
        enabled: !!tenantId && !areasLoading && query.length >= MIN_QUERY,
        staleTime: 30000,
        queryFn: async ()=>{
            const [companies, vehicles, library, labor, programs, pursuits, landscapes] = await Promise.allSettled([
                deliveryClient.listCompanyProfiles({
                    companyId: tenantId,
                    search: query
                }),
                deliveryClient.listContractVehicles({
                    companyId: tenantId
                }),
                projectDataClient.searchLibraryItems({
                    filter: {
                        scope: '',
                        query
                    },
                    offset: 0,
                    limit: PER_SOURCE
                }),
                deliveryClient.listLaborCategoryEntries({
                    companyId: tenantId,
                    companyProfileId: '',
                    search: query,
                    contractVehicleId: ''
                }),
                delivery ? deliveryClient.listPrograms({
                    companyId: tenantId,
                    search: query,
                    sectorId: '',
                    businessUnitId: '',
                    portfolioId: '',
                    operationalStateId: ''
                }) : NOT_QUERIED,
                capture ? projectDataClient.listCaptureProjects({}) : NOT_QUERIED,
                opportunities ? morgansClient.listLandscapes({}) : NOT_QUERIED
            ]);
            const hits = [];
            const unavailable = [];
            const names = new Map();
            const vehicleNames = new Map();
            if (companies.status === 'fulfilled') {
                for (const c of companies.value.profiles.slice(0, PER_SOURCE)){
                    names.set(c.id, c.name);
                    hits.push({
                        id: c.id,
                        kind: 'company',
                        title: c.name,
                        context: [
                            'Company',
                            c.isOwnCompany ? 'Your organization' : '',
                            c.cageCode && `CAGE ${c.cageCode}`
                        ].filter(Boolean).join(' · '),
                        href: registryRecordHref('company', c.id, '')
                    });
                }
            } else unavailable.push('company');
            if (vehicles.status === 'fulfilled') {
                for (const v of vehicles.value.vehicles)vehicleNames.set(v.id, v.name);
                for (const v of vehicles.value.vehicles.filter((v)=>has([
                        v.name,
                        v.contractNumber,
                        v.agencyAcronym
                    ], query)).slice(0, PER_SOURCE)){
                    hits.push({
                        id: v.id,
                        kind: 'vehicle',
                        title: v.name,
                        context: [
                            'Vehicle',
                            v.agencyAcronym,
                            v.contractNumber
                        ].filter(Boolean).join(' · '),
                        href: registryRecordHref('vehicle', v.id, '')
                    });
                }
            } else unavailable.push('vehicle');
            if (library.status === 'fulfilled') {
                for (const i of library.value.items.slice(0, PER_SOURCE)){
                    hits.push({
                        id: i.id,
                        kind: 'library',
                        title: i.title,
                        context: [
                            'Library',
                            i.itemType,
                            i.customerAgencyName,
                            i.scopeContext
                        ].filter(Boolean).join(' · '),
                        href: `/library?q=${encodeURIComponent(query)}&record=${encodeURIComponent(i.id)}`
                    });
                }
            } else unavailable.push('library');
            if (labor.status === 'fulfilled') {
                for (const l of labor.value.entries.slice(0, PER_SOURCE)){
                    hits.push({
                        id: l.id,
                        kind: 'labor',
                        title: l.title,
                        context: [
                            'Labor category',
                            names.get(l.companyProfileId) ?? '',
                            vehicleNames.get(l.contractVehicleId) ?? '',
                            l.source === 'CANDIDATE' ? 'candidate' : l.solicitationRef
                        ].filter(Boolean).join(' · '),
                        href: registryRecordHref('lcat_entry', l.id, '')
                    });
                }
            } else unavailable.push('labor');
            // `null` means the viewer cannot reach the source: not queried, not unavailable.
            if (programs.status === 'rejected') unavailable.push('program');
            else if (programs.value) {
                // The server search may not cover contract number, so match it here too.
                for (const p of programs.value.programs.filter((p)=>has([
                        p.programNameShort,
                        p.programNameFull,
                        p.contractNumber
                    ], query)).slice(0, PER_SOURCE)){
                    const title = p.programNameShort || p.programNameFull;
                    hits.push({
                        id: p.id,
                        kind: 'program',
                        title,
                        context: [
                            'Program',
                            p.programNameFull !== title && p.programNameFull,
                            p.contractNumber
                        ].filter(Boolean).join(' · '),
                        href: `/delivery/programs/${encodeURIComponent(p.id)}`
                    });
                }
            }
            if (pursuits.status === 'rejected') unavailable.push('pursuit');
            else if (pursuits.value) {
                for (const c of pursuits.value.captures.filter((c)=>has([
                        c.name
                    ], query)).slice(0, PER_SOURCE)){
                    hits.push({
                        id: c.captureProjectId,
                        kind: 'pursuit',
                        title: c.name,
                        context: [
                            'Pursuit',
                            LIFECYCLE[c.lifecycle]
                        ].filter(Boolean).join(' · '),
                        href: `/capture/${encodeURIComponent(c.captureProjectId)}`
                    });
                }
            }
            if (landscapes.status === 'rejected') unavailable.push('landscape');
            else if (landscapes.value) {
                for (const l of landscapes.value.landscapes.filter((l)=>has([
                        l.name
                    ], query)).slice(0, PER_SOURCE)){
                    hits.push({
                        id: l.id,
                        kind: 'landscape',
                        title: l.name,
                        context: [
                            'Landscape',
                            l.scope.length <= 40 && l.scope
                        ].filter(Boolean).join(' · '),
                        href: `/landscapes/${encodeURIComponent(l.id)}`
                    });
                }
            }
            return {
                hits,
                unavailable
            };
        }
    });
}
const GROUP = {
    company: 'Companies',
    vehicle: 'Vehicles',
    library: 'Library',
    labor: 'Labor categories',
    program: 'Programs',
    pursuit: 'Pursuits',
    landscape: 'Landscapes'
};
/**
 * The hits as command-palette items. Each carries its type and context as the
 * description so same-titled results stay distinguishable, and the typed query
 * as a keyword because the server — not the palette — decided it matches.
 */ export function useSharedRecordCommandItems(query) {
    const navigate = useNavigate();
    const search = useSharedRecordSearch(query);
    const items = useMemo(()=>(search.data?.hits ?? []).map((h)=>({
                id: `shared-${h.kind}-${h.id}`,
                label: h.title,
                description: h.context,
                group: GROUP[h.kind],
                keywords: [
                    query.trim(),
                    h.title
                ],
                testId: Testids.sharedRecord.searchResultType,
                itemProps: {
                    'data-kind': h.kind
                },
                onSelect: ()=>navigate(h.href)
            })), [
        search.data,
        query,
        navigate
    ]);
    return {
        items,
        loading: search.isFetching,
        unavailable: search.data?.unavailable ?? []
    };
}
